import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { LiveKitRoom, type LocalUserChoices } from '@livekit/components-react';
import { DisconnectReason } from 'livekit-client';
import type { JoinContextResponse, JoinResponse } from '@hiredude/meet-shared';

import { RotateCcw } from 'lucide-react';

import { CallStage } from '@/components/CallStage';
import { Loading, Notice } from '@/components/Notice';
import { PreJoinScreen } from '@/components/prejoin/PreJoinScreen';
import { ReconnectOverlay } from '@/components/ReconnectOverlay';
import { Button } from '@/components/ui/button';
import { exchangeForToken, fetchJoinContext, MeetApiError } from '@/lib/api';
import { buildConnectOptions, buildRoomOptions } from '@/lib/roomOptions';

type LeaveReason = 'left' | 'ended' | 'removed' | 'elsewhere';

/**
 * Disconnects that are the point of the product, rather than a fault.
 *
 * Anything outside this set is treated as a dropped call worth rescuing: the
 * SFU restarting, the signal socket dying, a resume the server can no longer
 * honour. Those all surface as ordinary disconnects once livekit-client has
 * exhausted its own retries.
 */
const TERMINAL_DISCONNECTS = new Set<DisconnectReason>([
  DisconnectReason.CLIENT_INITIATED,
  DisconnectReason.PARTICIPANT_REMOVED,
  DisconnectReason.ROOM_DELETED,
  DisconnectReason.DUPLICATE_IDENTITY,
  DisconnectReason.USER_REJECTED,
]);

const LEAVE_COPY: Record<
  LeaveReason,
  { title: string; body: string; tone: 'info' | 'error' | 'success'; canRejoin: boolean }
> = {
  left: {
    title: 'You left the call',
    body: 'You can close this tab. If you left by mistake, rejoin from the same link.',
    tone: 'info',
    canRejoin: true,
  },
  ended: {
    title: 'The interview has ended',
    body: 'Thanks for your time. The recording is on its way to the recruiter. You can rejoin from the same link if you need another session.',
    tone: 'success',
    canRejoin: true,
  },
  // Telling a removed candidate to "reopen your link to rejoin" would be both
  // wrong and a little cruel: the host meant to end it for them.
  removed: {
    title: 'The interviewer ended your call',
    body: 'You were removed from the interview. Contact the recruiter if you think this was a mistake.',
    tone: 'error',
    canRejoin: false,
  },
  elsewhere: {
    title: 'You joined from another device',
    body: 'This tab was disconnected because the same link was opened somewhere else.',
    tone: 'info',
    canRejoin: true,
  },
};

function leaveReason(reason?: DisconnectReason): LeaveReason {
  switch (reason) {
    case DisconnectReason.PARTICIPANT_REMOVED:
      return 'removed';
    case DisconnectReason.ROOM_DELETED:
      return 'ended';
    case DisconnectReason.DUPLICATE_IDENTITY:
      return 'elsewhere';
    default:
      return 'left';
  }
}

type Phase =
  | { name: 'loading' }
  | { name: 'error'; title: string; message: string }
  | { name: 'prejoin'; context: JoinContextResponse }
  | { name: 'connecting'; context: JoinContextResponse }
  | { name: 'in-call'; context: JoinContextResponse; session: JoinResponse; choices: LocalUserChoices }
  | { name: 'rejoining'; context: JoinContextResponse; choices: LocalUserChoices; attempt: number }
  | { name: 'left'; reason: LeaveReason };

/** How many times we re-run the join exchange before giving up on the call. */
const MAX_REJOIN_ATTEMPTS = 5;

export function JoinPage() {
  const { code = '' } = useParams();
  const [searchParams] = useSearchParams();
  const joinKey = searchParams.get('k') ?? '';

  const [phase, setPhase] = useState<Phase>({ name: 'loading' });

  // Held at the top level so the memo keys stay stable: rebuilding RoomOptions
  // mid-call would hand LiveKitRoom a new Room to connect.
  const activeChoices = phase.name === 'in-call' ? phase.choices : null;
  const videoDeviceId = activeChoices?.videoDeviceId ?? '';
  const audioDeviceId = activeChoices?.audioDeviceId ?? '';

  const roomOptions = useMemo(
    () => buildRoomOptions({ videoDeviceId, audioDeviceId }),
    [videoDeviceId, audioDeviceId],
  );
  const connectOptions = useMemo(() => buildConnectOptions(), []);

  useEffect(() => {
    if (!joinKey) {
      setPhase({
        name: 'error',
        title: 'This link is incomplete',
        message:
          'The link is missing its access key. Open the full link from your WhatsApp message rather than typing it in.',
      });
      return;
    }

    let cancelled = false;
    fetchJoinContext(code, joinKey)
      .then((context) => {
        if (!cancelled) setPhase({ name: 'prejoin', context });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setPhase({ name: 'error', ...describe(err) });
      });

    return () => {
      cancelled = true;
    };
  }, [code, joinKey]);

  /**
   * livekit-client gives up quietly when its own reconnect cannot be honoured,
   * which is exactly what happens when the SFU is restarted underneath a live
   * call: the resume fails, and because `auto_create` is off the full reconnect
   * gets a 404 for a room that no longer exists. Retrying the join exchange is
   * what fixes it — the API recreates the room and mints a fresh token — so a
   * deploy or a crash costs the interview a few seconds instead of ending it.
   */
  const handleDisconnected = useCallback((reason?: DisconnectReason) => {
    setPhase((current) => {
      if (current.name !== 'in-call') return current;
      if (reason === undefined || TERMINAL_DISCONNECTS.has(reason)) {
        return { name: 'left', reason: leaveReason(reason) };
      }
      return {
        name: 'rejoining',
        context: current.context,
        choices: current.choices,
        attempt: 1,
      };
    });
  }, []);

  useEffect(() => {
    if (phase.name !== 'rejoining') return;
    const { context, choices, attempt } = phase;

    let cancelled = false;
    // Back off so a server that is still booting is not hammered, but stay
    // short enough that a routine restart is barely noticed.
    const delayMs = Math.min(1000 * 2 ** (attempt - 1), 8000);

    const timer = setTimeout(() => {
      exchangeForToken(code, joinKey, choices.username)
        .then((session) => {
          if (!cancelled) setPhase({ name: 'in-call', context, session, choices });
        })
        .catch((err: unknown) => {
          if (cancelled) return;
          // A closed window or an ended meeting is a real answer, not a blip.
          if (err instanceof MeetApiError && err.status !== 0 && err.status < 500) {
            setPhase({ name: 'left', reason: 'ended' });
            return;
          }
          if (attempt >= MAX_REJOIN_ATTEMPTS) {
            setPhase({
              name: 'error',
              title: 'The call dropped',
              message: 'We could not get you back into the interview. Reopen your link to retry.',
            });
            return;
          }
          setPhase({ name: 'rejoining', context, choices, attempt: attempt + 1 });
        });
    }, delayMs);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [phase, code, joinKey]);

  const handleSubmit = useCallback(
    async (choices: LocalUserChoices) => {
      if (phase.name !== 'prejoin') return;
      const { context } = phase;
      setPhase({ name: 'connecting', context });

      try {
        // The token is minted now, not when the link was created, so it only
        // has to live as long as the call itself.
        const session = await exchangeForToken(code, joinKey, choices.username);
        setPhase({ name: 'in-call', context, session, choices });
      } catch (err) {
        setPhase({ name: 'error', ...describe(err) });
      }
    },
    [code, joinKey, phase],
  );

  switch (phase.name) {
    case 'loading':
      return <Loading label="Checking your interview link" />;

    case 'error':
      return (
        <Notice
          title={phase.title}
          tone="error"
          actions={
            <Button variant="outline" onClick={() => window.location.reload()}>
              <RotateCcw />
              Try again
            </Button>
          }
        >
          <p>{phase.message}</p>
        </Notice>
      );

    // The same overlay the in-call reconnect uses, so a restart that outlives
    // livekit-client's own retries looks like one continuous "Reconnecting…"
    // rather than the call visibly collapsing and rebuilding itself.
    case 'rejoining':
      return <ReconnectOverlay visible />;

    case 'left': {
      const { title, body, tone, canRejoin } = LEAVE_COPY[phase.reason];
      return (
        <Notice
          title={title}
          tone={tone}
          actions={
            canRejoin && (
              <Button onClick={() => window.location.reload()}>
                <RotateCcw />
                Rejoin the call
              </Button>
            )
          }
        >
          <p>{body}</p>
        </Notice>
      );
    }

    // The pre-join screen owns its own preview tracks and must sit outside
    // LiveKitRoom, which opens the same devices the moment it connects.
    case 'prejoin':
    case 'connecting':
      return (
        <PreJoinScreen
          context={phase.context}
          connecting={phase.name === 'connecting'}
          onSubmit={handleSubmit}
        />
      );

    case 'in-call': {
      const { session, choices, context } = phase;
      return (
        <LiveKitRoom
          token={session.token}
          serverUrl={session.wsUrl}
          connect
          video={choices.videoEnabled}
          audio={choices.audioEnabled}
          options={roomOptions}
          connectOptions={connectOptions}
          onDisconnected={handleDisconnected}
          onError={(err) =>
            setPhase({
              name: 'error',
              title: 'The call dropped',
              message: err.message || 'Something went wrong with the connection.',
            })
          }
          data-lk-theme="default"
        >
          <CallStage
            session={session}
            otherPartyName={context.otherPartyName}
            code={code}
            joinKey={joinKey}
            onLeave={(reason) => setPhase({ name: 'left', reason })}
          />
        </LiveKitRoom>
      );
    }
  }
}

function describe(err: unknown): { title: string; message: string } {
  if (!(err instanceof MeetApiError)) {
    return { title: 'Something went wrong', message: 'Please reopen your link and try again.' };
  }

  switch (err.status) {
    case 403:
      return { title: 'This link is not valid', message: err.message };
    case 404:
      return {
        title: 'We could not find this interview',
        message: 'Check that you opened the most recent link sent to you.',
      };
    case 410:
      return { title: 'This link has expired', message: err.message };
    case 0:
      return {
        title: 'You appear to be offline',
        message: 'Check your internet connection and reload this page.',
      };
    default:
      return { title: 'Something went wrong', message: err.message };
  }
}
